import type { ClientItemId } from 'owa-client-ids';
import EventScope from 'owa-service/lib/contract/EventScope';
import { CalendarEvent, initializeAsThisAndFollowingInstances } from 'owa-calendar-types';
import type { CreateCollabSpaceOnInitProps } from 'owa-calendar-helpers-types';
import initializeCalendarEvent from 'owa-calendar-forms-common/lib/utils/initializeCalendarEvent';
import { fetchFullCalendarEventAndUpdateItemResponseShapeType } from 'owa-calendar-forms-common/lib/helpers/eventStore';
import cloneDeep from 'lodash-es/cloneDeep';
import { canEdit } from 'owa-calendar-event-capabilities';
import { toJS } from 'mobx';
import friendlyEndDateForAllDay from 'owa-calendar-forms-common/lib/utils/friendlyEndDateForAllDay';
import { getUserTimeZone } from 'owa-session-store';
import { owaDate } from 'owa-datetime';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getFtdfCreateCollabSpaceOnInitProps } from './collabSpaceComposeUtils';
import cloneEvent from 'owa-calendar-forms-common/lib/utils/cloneEvent';

export async function initializeEventsForEdit(
    fetchSource: string,
    eventScope: EventScope,
    itemId?: ClientItemId,
    eventUpdates?: Partial<CalendarEvent>,
    itemHasUpdates?: boolean,
    shouldRespectStartEndTime?: boolean
): Promise<{
    originalEvent: CalendarEvent | null;
    event: CalendarEvent | null;
    createCollabSpaceOnInitProps: CreateCollabSpaceOnInitProps | null;
}> {
    let originalEvent = null;
    let event = null;
    let createCollabSpaceOnInitProps = null;

    const isEditExistingEvent = itemId?.Id;

    if (isEditExistingEvent) {
        originalEvent = await fetchExistingEventToEdit(
            itemId,
            eventUpdates,
            eventScope,
            fetchSource
        );

        if (!originalEvent) {
            // Event not found
            return {
                originalEvent: null,
                event: null,
                createCollabSpaceOnInitProps: null,
            };
        }

        if (eventScope == EventScope.ThisAndFollowingInstances) {
            originalEvent = updateEventForFTDF(
                originalEvent /* seriesMaster */,
                fetchSource,
                itemId /* instanceId */
            );

            if (isFeatureEnabled('cal-cmp-fluidCollaborativeSpace') && originalEvent.CollabSpace) {
                createCollabSpaceOnInitProps = await getFtdfCreateCollabSpaceOnInitProps(
                    originalEvent
                );
                originalEvent.CollabSpace = null;
            }
        }
    } else {
        originalEvent = initializeCalendarEvent(
            eventUpdates,
            false /* isInitializedEvent */,
            shouldRespectStartEndTime
        );
    }

    if (originalEvent.IsAllDayEvent) {
        originalEvent.End = friendlyEndDateForAllDay(originalEvent.End);
    }

    normalizeTimeZonesForEvent(originalEvent);

    event = eventFromOriginalEvent(originalEvent, itemHasUpdates ? eventUpdates : null);

    return {
        originalEvent: cloneEvent(originalEvent), // Use cloneEvent to deep copy and initialize any null/undefined arrays
        event: event,
        createCollabSpaceOnInitProps: createCollabSpaceOnInitProps,
    };
}

async function fetchExistingEventToEdit(
    itemId: ClientItemId,
    eventUpdates: Partial<CalendarEvent>,
    eventScope: EventScope,
    fetchSource: string
): Promise<CalendarEvent | null> {
    let fullItem = null;

    // For existing events, retrieve the most up to date version from server.
    // Use fetchFullCalendarEventAndUpdateItemResponseShapeType with 'WithBlockedExternalContent'
    // to unblock external images since we are in a compose scenario.
    const { fullEventPromise } = fetchFullCalendarEventAndUpdateItemResponseShapeType(
        itemId,
        'WithBlockedExternalContent',
        null,
        () =>
            // in most situations, we want to pass the actual item here, rather than a deep clone, because loading the full item from the
            // cache will overwrite the partial item's properties. However, in this case, we're passing the UPDATED item as the partial item
            // which we don't want the properties to be overwritten for because we'll lose those updates.
            cloneDeep(eventUpdates) as CalendarEvent /* getExistingCalendarEvent */,
        eventScope === EventScope.ThisAndFollowingInstances ||
            eventScope === EventScope.AllInstancesInSeries,
        { fetchSource: fetchSource }
    );

    // We are creating a copy as we are mutating the object here so we dont want to mutate the store instance
    fullItem = toJS(await fullEventPromise);

    if (!canEdit(fullItem)) {
        // If you cannot edit the item, then return.
        // This might happen in the deeplink scenario where the user can pass
        // any item id in the url
        return null;
    }

    return fullItem;
}

function updateEventForFTDF(
    seriesMaster: CalendarEvent,
    fetchSource: string,
    instanceId: ClientItemId
): CalendarEvent {
    const instanceItem = fetchFullCalendarEventAndUpdateItemResponseShapeType(
        instanceId,
        'WithBlockedExternalContent',
        null /* folderId */,
        null /* getExistingCalendarEvent */,
        false /* isfetchFullSeriesMasterCalendarEventFromEventInstance */,
        { fetchSource: fetchSource }
    ).event;

    // For editing this and following instances we need to make a hybrid item:
    const hybridItem = { ...seriesMaster };
    initializeAsThisAndFollowingInstances(hybridItem, instanceItem);

    return hybridItem;
}

function normalizeTimeZonesForEvent(event: CalendarEvent) {
    // Normalize time zones in the event, so the whole form runs out of Start.tz and End.tz.
    const userTimeZone = getUserTimeZone();
    event.StartTimeZoneId = event.StartTimeZoneId || userTimeZone;
    event.EndTimeZoneId = event.EndTimeZoneId || userTimeZone;
    event.Start = owaDate(event.StartTimeZoneId, event.Start);
    event.End = owaDate(event.EndTimeZoneId, event.End);
}

import assign from 'object-assign';
function eventFromOriginalEvent(
    originalEvent: CalendarEvent,
    eventUpdates: Partial<CalendarEvent> | null
) {
    const event = cloneEvent(originalEvent);
    if (eventUpdates) {
        assign(event, eventUpdates);

        // Normalize to handle cases where the updates changed the times
        normalizeTimeZonesForEvent(event);
    }

    return event;
}
